<template>
  <div>
    <app-cells><h1 class="title">Authorization forms</h1></app-cells>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <div v-if="!authorization_forms.length" class="no-results">
        <img src="@/assets/img/schedule-plug-icon.svg" alt="">
        <div class="no-results__title">No authorization forms</div>
      </div>
      <div v-else class="info-cards">
        <button
          v-for="form of authorization_forms"
          :key="form.id"
          class="info-cards__card"
          @click="onShowModal(form)"
        >
          <span class="info-cards__row"><span>Completed:</span><span>{{ dateTimeFormat(form.auth_form_data.completion_date) }}</span></span>
          <span class="info-cards__row"><span>Invoice:</span><span>{{ form.number }}</span></span>
          <span class="info-cards__row"><span>Card number:</span><span>{{ form.auth_form_data.card_number }}</span></span>
          <span class="info-cards__row"><span>Valid until:</span><span>{{ dateUntilFormat(form.auth_form_data.valid_until) }}</span></span>
          <span class="info-cards__row"><span>CVV:</span><span>{{ form.auth_form_data.cvv }}</span></span>
          <span class="info-cards__row"><span>Payable:</span><span>{{ formatString(form.amount_payable_usd) }} USD</span></span>
        </button>
      </div>
      <app-cells v-if="pagination.pages > 1" position="center">
        <paginate
          :clickHandler="onClickPagination"
          v-model="pagination.page"
          :page-count="pagination.pages"
          :prev-text="'<'"
          :next-text="'>'"
          :container-class="'pagination'"
          :page-class="'pagination__item'"
          :page-link-class="'pagination__link'"
          :prev-class="'pagination__item pagination__item--prev'"
          :next-class="'pagination__item pagination__item--next'"
          :next-link-class="'pagination__link pagination__link--next'"
          :prev-link-class="'pagination__link pagination__link--prev'"
        />
      </app-cells>
    </template>
    <modal
      name="receipt-modal"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      :shiftY="0"
      transition="pop-out"
    >
      <div class="popup">
        <button @click="$modal.hide('receipt-modal')" class="popup__close" type="button"></button>
        <div class="popup__title">Request authorization form charge</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Amount ($)" label-for="amount" required>
              <app-fin-number
                v-model="form.amount"
                :error="$v.form.amount.$error"
                id="amount"
                name="amount"
                placeholder="0"
                inputmode="decimal"
              />
              <template #error>
                <div v-if="$v.form.amount.$dirty && !$v.form.amount.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Bank" required>
              <app-cells :indent="false">
                <app-radio
                  v-for="radio of bank_options"
                  :key="radio.id"
                  v-model="form.bank"
                  :value="radio.id"
                  name="filterBank"
                  :checked="form.bank === radio.id"
                >
                  <template #label>{{ radio.short_name }}</template>
                </app-radio>
              </app-cells>
            </app-form-group>
            <app-form-group label="Channel" required>
              <app-cells :indent="false">
                <app-radio
                  v-for="radio of channel_options"
                  :key="radio.code"
                  v-model="form.channel"
                  :value="radio.code"
                  name="filterCreditCard"
                  :checked="form.channel === radio.code"
                >
                  <template #label>{{ radio.name }}</template>
                </app-radio>
              </app-cells>
            </app-form-group>
            <app-form-group label="RRN" required label-for="form_rrn">
              <app-input
                v-model="form.rrn"
                :error="$v.form.rrn.$error"
                id="form_rrn"
                name="form_rrn"
                placeholder="RRN"
                type="number"
                inputmode="numeric"
              />
              <template #error>
                <div v-if="$v.form.rrn.$dirty && !$v.form.rrn.required">Required field</div>
                <div v-if="$v.form.rrn.$dirty && !$v.form.rrn.maxLength">Length of RRN must be equal to 12</div>
                <div v-if="$v.form.rrn.$dirty && !$v.form.rrn.minLength">Length of RRN must be equal to 12</div>
              </template>
            </app-form-group>
            <app-form-group label="Machine slip" required>
              <app-uploader
                v-model="form.machine_slip"
                :extensions="['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip', 'application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp']"
                formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
              />
              <template #error>
                <div v-if="$v.form.machine_slip.$dirty && !$v.form.machine_slip.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button type="submit" ref="submit">Charged</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { format, parseISO } from 'date-fns'
import { getBanks, getCardTypes, getFinanceInvoices, patchFinanceInvoiceAuthData } from '@/http'
import { required, maxLength, minLength } from 'vuelidate/lib/validators'

export default {
  name: 'ClientsAuthorizationForms',
  data() {
    return {
      page_loaded: false,
      authorization_forms: [],
      bank_options: [],
      channel_options: [],
      form: {},
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      item_to_action: null,
    }
  },
  validations: {
    form: {
      amount: { required },
      rrn: {
        required,
        maxLength: maxLength(12),
        minLength: minLength(12),
      },
      machine_slip: { required },
      bank: { required },
      channel: { required },
    }
  },
  created() {
    getBanks({pos: true}).then(response => { this.bank_options = response.data })
    getCardTypes().then(response => { this.channel_options = response.data })
    this.fetchItems()
  },
  methods: {
    async fetchItems() {
      const response = await getFinanceInvoices({
        page: this.pagination.page,
        page_size: 5,
        authorization_form_charged:false,
        authorization_form_completed:true
      })
      this.authorization_forms = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.page_loaded = true
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      patchFinanceInvoiceAuthData(this.item_to_action.id, { charged: true, pos_transaction: {...this.form, machine_slip: this.form.machine_slip.id} })
        .then(() => {
          document.body.scrollIntoView()
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Data saved',
          })
          this.$modal.hide('receipt-modal')
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    onShowModal(item) {
      this.item_to_action = item
      this.form = {
        amount: item.amount_payable_with_commission,
      }
      this.$v.form.$reset()
      this.$modal.show('receipt-modal')
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    dateTimeFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy HH:mm') : '-'
    },
    dateUntilFormat(date) {
      return date ? format(parseISO(date), 'MM / yyyy') : '-'
    },
    formatString(string) {
      return string ? new Intl.NumberFormat('en-US').format(string) : '-'
    },
  }
}
</script>
